import "./StreamingChat.scss";
import "../../components/Ai/Ai.style.scss";

import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";
import remarkGfm from "remark-gfm";

import Icon from "../../components/Icon/Icon";
import InputTextarea from "../../components/InputTextArea/InputTextArea";
import ShimmerLogo from "../../components/ShimmerLogo/ShimmerLogo";
import { useConversationMemory } from "../../hooks/useConversationMemory";
import { sanitizeDashboardData } from "./sanitizeDashboardData";

/**
 * Renders assistant and user message content as GitHub-flavored markdown.
 *
 * @param {string} content - The message text to render.
 * @returns {React.ReactElement} Markdown-rendered message content.
 */
const defaultRenderMessageContent = (content) => (
	<ReactMarkdown rehypePlugins={[]} remarkPlugins={[remarkGfm]}>
		{content}
	</ReactMarkdown>
);

/**
 * Render streaming assistant content as plain text until the stream completes.
 *
 * Rendering partial markdown on every chunk causes large DOM reshapes for lists,
 * tables, and headings, which can make the chat panel flicker and bounce while
 * a tool-driven response is still arriving.
 *
 * @param {string} content - The current streamed text buffer.
 * @returns {React.ReactElement} Plain text streaming content.
 */
const renderStreamingMessageContent = (content) => <div className='ai-response'>{content}</div>;

// Token the AI embeds when it wants the "Create Score Insights Dashboard" button to appear
const SCORE_INSIGHTS_BUTTON_TOKEN = "[SHOW_SCORE_INSIGHTS_BUTTON]";

// Maximum number of characters a user can send in a single message
const MAX_MESSAGE_LENGTH = 225;
const MAX_FEEDBACK_LENGTH = 150;

const createMessageId = () => {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
		return crypto.randomUUID();
	}

	return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const createChatMessage = (role, content, extra = {}) => ({
	id: createMessageId(),
	role,
	content,
	...extra,
});

const ensureMessageHasId = (message) =>
	message?.id ? message : createChatMessage(message?.role ?? "assistant", message?.content ?? "", message ?? {});

const getFeedbackStorageKey = (conversationKey) => `streaming-chat-feedback:${conversationKey}`;

const findPreviousUserMessage = (messages, assistantMessageIndex) => {
	for (let index = assistantMessageIndex - 1; index >= 0; index -= 1) {
		if (messages[index]?.role === "user") {
			return messages[index].content ?? "";
		}
	}

	return "";
};

const defaultChatApiEndpoint = ["DEV", "STAGING"].includes(import.meta.env.VITE_REACT_APP_API_ENVIRONMENT)
	? "/api/v3/dashboard/stream-analyze"
	: "/chat.php";

const buildResumeDashboardData = (dashboardData, dashboardType) => ({
	type: dashboardType,
	dashboard_type: dashboardType,
	...(dashboardData?.dashboard_id ? { dashboard_id: dashboardData.dashboard_id } : {}),
	...(dashboardData?.dashboardId ? { dashboardId: dashboardData.dashboardId } : {}),
	...(dashboardData?.id ? { id: dashboardData.id } : {}),
	...(dashboardData?.ai_context ? { ai_context: dashboardData.ai_context } : {}),
	...(dashboardData?.ai_call_params?.dashboard_id
		? { ai_call_params: { dashboard_id: dashboardData.ai_call_params.dashboard_id } }
		: {}),
});

/**
 * Opens the existing Score Insights creation panel by triggering its sidebar item.
 *
 * This preserves the current dashboard navigation behavior instead of duplicating
 * the sidebar state logic in the chat component.
 */
const triggerScoreInsightsSidebarItem = () => {
	document.getElementById("side-bar-score-insights-creation-id")?.click();
};

/**
 * Renders message content and replaces the Score Insights token with the real action button.
 *
 * @param {string} content - The message content returned by the assistant.
 * @param {number|string} idx - Stable render key context passed to the content renderer.
 * @param {boolean} isStreaming - Whether this content is still being streamed.
 * @param {Function} renderMessageContent - Renderer used for normal text content.
 * @param {Function|null} setParentModalState - Optional parent modal state setter.
 * @returns {React.ReactNode} Rendered message content with optional action button.
 */
const renderContentWithButton = (content, idx, isStreaming, renderMessageContent, setParentModalState) => {
	if (!content.includes(SCORE_INSIGHTS_BUTTON_TOKEN)) {
		return renderMessageContent(content, idx, isStreaming);
	}

	const parts = content.split(SCORE_INSIGHTS_BUTTON_TOKEN);

	return (
		<>
			{parts[0] && renderMessageContent(parts[0].trim(), idx, isStreaming)}
			<button
				className='btn btn-primary'
				onClick={() => {
					triggerScoreInsightsSidebarItem();
					if (setParentModalState) {
						setParentModalState(false);
					}
				}}
			>
				Create Score Insights Dashboard//
			</button>
			{parts[1] && renderMessageContent(parts[1].trim(), idx, isStreaming)}
		</>
	);
};

/**
 * Streaming dashboard AI chat component.
 *
 * Sends dashboard context and chat history to the backend, reads the streamed SSE
 * response, stores the backend-owned Responses API conversation id, and reuses
 * that id on follow-up messages so the backend can resume the same AI conversation.
 *
 * @param {Object} props - Component props.
 * @param {string} props.dashboardType - Dashboard type used for memory scoping and backend context.
 * @param {string} props.conversationKey - Key used to scope in-memory chat history.
 * @param {Object} props.dashboardData - Dashboard payload sent to the analysis endpoint.
 * @param {boolean} props.hideInput - Whether to hide the user message input.
 * @param {Function} props.onMessagesChange - Optional callback exposing message controls to a parent.
 * @param {Function|null} props.setParentModalState - Optional parent modal state setter.
 * @param {Function} props.renderMessageContent - Custom message renderer.
 * @param {string} props.apiEndpoint - API endpoint for the streaming analysis request.
 * @param {string} props.initialMessage - Message shown before the first user message.
 * @param {string} props.closingMessage - Message shown after the conversation has messages.
 * @param {boolean} props.persistInitialMessage - Whether to keep the initial message visible after messages exist.
 * @param {boolean} props.initialMessageIsHtml - Whether the initial or closing message contains HTML.
 * @param {string} props.initialMessageColor - Optional background color for initial or closing messages.
 * @param {string} props.assistantName - Display name for assistant messages.
 * @param {React.ReactNode} props.initialMessageAction - Optional action rendered below the initial message.
 * @returns {React.ReactElement} Streaming chat UI.
 */
const StreamingChat = ({
	dashboardType = "",
	conversationKey = dashboardType,
	dashboardData,
	hideInput = false,
	onMessagesChange,
	setParentModalState = null,
	renderMessageContent = defaultRenderMessageContent,
	apiEndpoint = import.meta.env.VITE_CHAT_API_ENDPOINT || defaultChatApiEndpoint,
	initialMessage = `Hi there! I'm ${
		import.meta.env.VITE_APP_NAME
	}  AI, a helpful dashboard analysis assistant. What can I help you with today?`,
	closingMessage = "",
	persistInitialMessage = false,
	initialMessageIsHtml = false,
	initialMessageColor,
	assistantName = `${import.meta.env.VITE_APP_NAME} AI`,
	initialMessageAction = null,
}) => {
	const token = useSelector((state) => state.token);

	// Remember this chat's history while it is closed.
	const { savedMessages, saveMessages } = useConversationMemory(conversationKey);

	const [userInput, setUserInput] = useState("");

	// Start with any messages we remembered from before the chat was closed.
	// This reads the saved history once, when the chat first opens.
	const [messages, setMessages] = useState(() => savedMessages.map(ensureMessageHasId));

	const [streamedAnswer, setStreamedAnswer] = useState("");
	const [loading, setLoading] = useState(false);
	const [errorText, setErrorText] = useState("");
	const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
	const [feedbackDraft, setFeedbackDraft] = useState("");
	const [feedbackError, setFeedbackError] = useState("");
	const [activeFeedback, setActiveFeedback] = useState(null);
	const [submittedFeedbackByMessage, setSubmittedFeedbackByMessage] = useState({});
	const chatRootRef = useRef(null);
	const chatWrapperRef = useRef(null);
	const conversationIdRef = useRef(null);
	const hasInitialMessage = initialMessage || initialMessageAction;
	const showInitialMessage = hasInitialMessage && (persistInitialMessage || messages.length === 0);
	const hasAssistantMessages = messages.some((message) => message.role === "assistant");

	useEffect(() => {
		try {
			const savedFeedback = window.sessionStorage.getItem(getFeedbackStorageKey(conversationKey));
			setSubmittedFeedbackByMessage(savedFeedback ? JSON.parse(savedFeedback) : {});
		} catch {
			setSubmittedFeedbackByMessage({});
		}

		setActiveFeedback(null);
		setFeedbackDraft("");
		setFeedbackError("");
		setFeedbackSubmitting(false);
	}, [conversationKey]);

	useEffect(() => {
		try {
			window.sessionStorage.setItem(
				getFeedbackStorageKey(conversationKey),
				JSON.stringify(submittedFeedbackByMessage),
			);
		} catch {
			// Ignore storage failures. The UI can still enforce feedback once per message in memory.
		}
	}, [conversationKey, submittedFeedbackByMessage]);

	// Whenever the messages change, save them so they come back if the chat is
	// closed and reopened. Only finished messages are stored here; the answer
	// that is still being typed out lives in its own separate state.
	useEffect(() => {
		saveMessages(messages);
	}, [messages, saveMessages]);

	useEffect(() => {
		setMessages(savedMessages.map(ensureMessageHasId));
		setStreamedAnswer("");
		setErrorText("");
		setLoading(false);
		conversationIdRef.current = null;
	}, [conversationKey]);

	// Expose message manipulation functions to parent via callback
	useEffect(() => {
		if (onMessagesChange) {
			onMessagesChange({
				addMessage: (message) => {
					setMessages((prev) => [...prev, ensureMessageHasId(message)]);
				},
				addStreamedMessage: (content) => {
					setStreamedAnswer(content);
				},
				setLoading: (isLoading) => {
					setLoading(isLoading);
				},
				setError: (error) => {
					setErrorText(error);
				},
				clearStreamed: () => {
					setStreamedAnswer("");
				},
				finalizeStreamedMessage: () => {
					if (streamedAnswer) {
						setMessages((prev) => [...prev, createChatMessage("assistant", streamedAnswer)]);
						setStreamedAnswer("");
					}
				},
			});
		}
	}, [onMessagesChange, streamedAnswer]);

	/**
	 * Sends the current user message to the streaming analysis endpoint.
	 *
	 * The first successful stream returns a backend-owned conversation id. That
	 * id is kept in a ref and sent with later requests so the backend can load
	 * and persist the correct Responses API conversation state.
	 *
	 * @param {React.FormEvent|React.KeyboardEvent} e - Submit or keyboard event.
	 * @returns {Promise<void>}
	 */
	const handleSubmit = async (e) => {
		if (e && e.preventDefault) {
			e.preventDefault();
		}

		const trimmedInput = userInput.trim();
		if (!trimmedInput) {
			return;
		}

		if (trimmedInput.length > MAX_MESSAGE_LENGTH) {
			setErrorText(`Your message exceeds the ${MAX_MESSAGE_LENGTH} character limit.`);
			return;
		}

		const newMessages = [...messages, createChatMessage("user", trimmedInput)];
		setMessages(newMessages);
		setUserInput("");
		setStreamedAnswer("");
		setErrorText("");
		setLoading(true);

		let trimmedDashboardData = sanitizeDashboardData(dashboardData);

		trimmedDashboardData = {
			...trimmedDashboardData,
			type: dashboardType,
			dashboard_type: dashboardType,
		};

		const hasConversationId = Boolean(conversationIdRef.current);
		const requestMessages = hasConversationId ? [{ role: "user", content: trimmedInput }] : newMessages;
		const requestDashboardData = hasConversationId
			? buildResumeDashboardData(dashboardData, dashboardType)
			: trimmedDashboardData;

		try {
			const response = await fetch(`${import.meta.env.VITE_REACT_APP_API_URL}${apiEndpoint}`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				body: JSON.stringify({
					messages: requestMessages,
					dashboardData: requestDashboardData,
					...(conversationIdRef.current ? { conversation_id: conversationIdRef.current } : {}),
				}),
			});

			if (!response.ok || !response.body) {
				// Try to read the server's error message before falling back to a generic one.
				// The backend returns { error: "..." } for validation failures like the character limit.
				let errorMessage = "Unable to connect to the analysis service. Try again later";
				try {
					const errorBody = await response.json();
					if (errorBody?.error) {
						errorMessage = errorBody.error;
					}
				} catch {
					// Response body was not JSON, so use the generic message.
				}
				throw new Error(errorMessage);
			}

			const reader = response.body.getReader();
			const decoder = new TextDecoder("utf-8");

			let buffer = "";
			let assistantText = "";

			while (true) {
				const { value, done } = await reader.read();
				if (done) break;

				buffer += decoder.decode(value, { stream: true });
				const events = buffer.split("\n\n");
				buffer = events.pop() || "";

				for (const event of events) {
					const line = event.trim();
					if (!line.startsWith("data:")) continue;

					const jsonText = line.slice(5).trim();
					if (!jsonText || jsonText === "[DONE]") continue;

					let data;
					try {
						data = JSON.parse(jsonText);
					} catch (err) {
						console.error("Failed to parse SSE chunk:", err);
						continue;
					}

					if (data.type === "conversation" && data.conversation_id) {
						conversationIdRef.current = data.conversation_id;
						continue;
					}

					if (data.role === "system" && data.content) {
						setErrorText(data.content);
						setStreamedAnswer("");
						assistantText = "";
						continue;
					}

					if (data.content) {
						assistantText += data.content;
						setStreamedAnswer(assistantText);
					}
				}
			}

			if (assistantText) {
				setMessages([...newMessages, createChatMessage("assistant", assistantText)]);
				setStreamedAnswer("");
			}

			setLoading(false);
		} catch (err) {
			console.error("Streaming error:", err);
			setErrorText(err.message || "An unexpected error occurred.");
			setLoading(false);
		}
	};

	/**
	 * Submits the chat when Enter is pressed inside the textarea.
	 *
	 * Shift+Enter is left alone so users can insert a newline.
	 *
	 * @param {React.KeyboardEvent} e - Keydown event from the chat form.
	 */
	const handleKeyDown = (e) => {
		if (e.target.tagName === "TEXTAREA" && e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSubmit(e);
		}
	};

	const openFeedbackForm = (messageId, feedbackType, messageContent, previousUserMessage) => {
		if (submittedFeedbackByMessage[messageId]) {
			return;
		}

		setActiveFeedback({ messageId, feedbackType, messageContent, previousUserMessage });
		setFeedbackDraft("");
		setFeedbackError("");
	};

	const closeFeedbackForm = () => {
		setActiveFeedback(null);
		setFeedbackDraft("");
		setFeedbackError("");
	};

	const handleFeedbackSubmit = async (e) => {
		e.preventDefault();

		if (!activeFeedback?.messageId) {
			return;
		}

		if (!conversationIdRef.current) {
			setFeedbackError("Feedback can only be sent for the current active chat session.");
			return;
		}

		const trimmedFeedback = feedbackDraft.trim();
		if (!trimmedFeedback) {
			setFeedbackError("Feedback message is required.");
			return;
		}

		if (trimmedFeedback.length > MAX_FEEDBACK_LENGTH) {
			setFeedbackError(`Feedback must be ${MAX_FEEDBACK_LENGTH} characters or less.`);
			return;
		}

		setFeedbackError("");
		setFeedbackSubmitting(true);

		try {
			const response = await fetch(`${import.meta.env.VITE_REACT_APP_API_URL}/api/v3/ai-feedback`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				body: JSON.stringify({
					conversation_id: conversationIdRef.current,
					assistant_message_id: activeFeedback.messageId,
					assistant_message_content: activeFeedback.messageContent,
					previous_user_message: activeFeedback.previousUserMessage,
					feedback_type: activeFeedback.feedbackType,
					feedback_text: trimmedFeedback,
				}),
			});

			if (!response.ok) {
				let errorMessage = "Unable to send feedback right now.";
				try {
					const errorBody = await response.json();
					if (errorBody?.error) {
						errorMessage = errorBody.error;
					}
				} catch {
					// Response body was not JSON, so use the generic error.
				}
				throw new Error(errorMessage);
			}

			setSubmittedFeedbackByMessage((prev) => ({
				...prev,
				[activeFeedback.messageId]: {
					feedbackType: activeFeedback.feedbackType,
					message: trimmedFeedback,
				},
			}));
			closeFeedbackForm();
		} catch (err) {
			setFeedbackError(err.message || "Unable to send feedback right now.");
		} finally {
			setFeedbackSubmitting(false);
		}
	};

	useEffect(() => {
		const container = chatWrapperRef.current;
		if (!container) return;

		container.scrollTo({
			top: container.scrollHeight,
			behavior: streamedAnswer ? "auto" : "smooth",
		});
	}, [messages, streamedAnswer]);

	useEffect(() => {
		if (!chatRootRef.current) return;

		const tables = chatRootRef.current.querySelectorAll("table");

		tables.forEach((table) => {
			const headerCells = Array.from(table.querySelectorAll("thead th"));
			if (!headerCells.length) return;

			const headerTexts = headerCells.map((th) => th.textContent?.trim() || "");

			const bodyRows = table.querySelectorAll("tbody tr");
			bodyRows.forEach((row) => {
				const cells = Array.from(row.querySelectorAll("td"));
				cells.forEach((td, index) => {
					const label = headerTexts[index];
					if (label) td.setAttribute("data-label", label);
				});
			});
		});
	}, [messages, streamedAnswer]);

	useEffect(() => {
		setTimeout(() => {
			document.getElementById("ai-chat-textarea").focus();
		}, 500);
	}, []);
	return (
		<div className='bluechip-ai' ref={chatRootRef}>
			<div className='chat-wrapper' ref={chatWrapperRef}>
				{showInitialMessage && (
					<div
						className='system-msg'
						style={initialMessageColor ? { backgroundColor: initialMessageColor } : undefined}
					>
						<strong className='block mb-1'>{assistantName}</strong>

						{initialMessage &&
							(initialMessageIsHtml ? (
								<div
									dangerouslySetInnerHTML={{
										__html: initialMessage,
									}}
								/>
							) : (
								renderMessageContent(initialMessage, "initial", false)
							))}

						{initialMessageAction && <div className='initial-message-action'>{initialMessageAction}</div>}
					</div>
				)}

				{messages.map((msg, idx) => {
					const feedbackAlreadySubmitted = Boolean(submittedFeedbackByMessage[msg.id]);
					const feedbackIsActive = activeFeedback?.messageId === msg.id;
					const previousUserMessage = msg.role === "assistant" ? findPreviousUserMessage(messages, idx) : "";

					return (
						<div
							key={msg.id ?? idx}
							className={`msg ${msg.role == "user" ? "user-msg user" : "system-msg"}`}
						>
							<strong className='block mb-1'>{msg.role === "user" ? "You" : assistantName}</strong>

							{msg.isHtml ? (
								<div dangerouslySetInnerHTML={{ __html: msg.content }} />
							) : (
								renderContentWithButton(
									msg.content,
									idx,
									false,
									renderMessageContent,
									setParentModalState,
								)
							)}

							{msg.role === "assistant" && hasAssistantMessages && (
								<div className='ai-feedback-actions'>
									<button
										type='button'
										className={`ai-feedback-button ${
											feedbackIsActive && activeFeedback?.feedbackType === "up" ? "selected" : ""
										}`}
										onClick={() => openFeedbackForm(msg.id, "up", msg.content, previousUserMessage)}
										disabled={feedbackAlreadySubmitted || feedbackSubmitting}
										aria-label='Send thumbs up feedback'
									>
										<Icon icon='thumbs-up' size='md' className='ai-feedback-icon' />
									</button>
									<button
										type='button'
										className={`ai-feedback-button ${
											feedbackIsActive && activeFeedback?.feedbackType === "down"
												? "selected"
												: ""
										}`}
										onClick={() =>
											openFeedbackForm(msg.id, "down", msg.content, previousUserMessage)
										}
										disabled={feedbackAlreadySubmitted || feedbackSubmitting}
										aria-label='Send thumbs down feedback'
									>
										<Icon icon='thumbs-down' size='sm' className='ai-feedback-icon' />
									</button>
									{feedbackAlreadySubmitted && (
										<span className='ai-feedback-status'>Feedback sent</span>
									)}
								</div>
							)}
						</div>
					);
				})}

				{messages.length !== 0 && closingMessage && (
					<div
						className='system-msg'
						style={initialMessageColor ? { backgroundColor: initialMessageColor } : undefined}
					>
						<strong className='block mb-1'>{assistantName}</strong>

						{initialMessageIsHtml ? (
							<div
								className='text-center'
								dangerouslySetInnerHTML={{
									__html: closingMessage,
								}}
							/>
						) : (
							renderMessageContent(closingMessage, "initial", false)
						)}
					</div>
				)}

				{loading && !errorText && (
					<div className='system-msg'>
						<strong className='llm-name'>{assistantName}</strong>
						{streamedAnswer ? (
							renderContentWithButton(
								streamedAnswer,
								"streaming",
								true,
								renderStreamingMessageContent,
								setParentModalState,
							)
						) : (
							<div className='w-100 ai-loading position-relative d-block'>
								<ShimmerLogo />
							</div>
						)}
					</div>
				)}

				{errorText && (
					<div className='error-msg text-white text-center'>
						<strong className='block mb-1'>Error: </strong>
						<span>{errorText}</span>
					</div>
				)}
			</div>

			{activeFeedback && (
				<form className='ai-feedback-form' onSubmit={handleFeedbackSubmit}>
					<div className='ai-feedback-form-header'>
						<strong>
							{activeFeedback.feedbackType === "up" ? "Thumbs up feedback" : "Thumbs down feedback"}
						</strong>
						<button type='button' className='ai-feedback-cancel' onClick={closeFeedbackForm}>
							Cancel
						</button>
					</div>
					<InputTextarea
						label='Tell us more about this response'
						srOnlyLabel
						theme='gray-outline'
						className='ai-feedback-textarea'
						placeholder='Share feedback about this response'
						value={feedbackDraft}
						onValueChange={setFeedbackDraft}
						maxLength={MAX_FEEDBACK_LENGTH}
						disabled={feedbackSubmitting}
					/>
					{feedbackError && <div className='ai-feedback-error'>{feedbackError}</div>}
					<div className='ai-feedback-form-actions'>
						<button
							type='button'
							className='btn-sm sm btn btn-secondary'
							onClick={closeFeedbackForm}
							disabled={feedbackSubmitting}
						>
							CANCEL
						</button>
						<button
							type='submit'
							className='btn-sm sm btn btn-primary primary'
							disabled={feedbackSubmitting}
						>
							{feedbackSubmitting ? "SENDING..." : "SEND"}
						</button>
					</div>
				</form>
			)}

			{!hideInput && (
				<form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
					<InputTextarea
						id={"ai-chat"}
						label='What would like to know about your dashboard?'
						srOnlyLabel
						theme='gray-outline'
						className='flex-1'
						placeholder='What would like to know about your dashboard?'
						value={userInput}
						onValueChange={setUserInput}
						maxLength={MAX_MESSAGE_LENGTH}
						disabled={loading}
					/>
					<button type='submit' disabled={loading} className='btn-sm sm btn btn-primary primary'>
						Send
					</button>
				</form>
			)}
		</div>
	);
};

export default StreamingChat;
